import type {
  ApiResponse,
  AppleAuthRequest,
  AppleAuthSetupRequiredResponse,
  AuthResponse,
  CompleteRegistrationRequest,
  GoogleAuthRequest,
  GoogleAuthSetupRequiredResponse,
  LoginRequest,
  MeasurementPreferences,
  RegisterRequest,
  StartSupportMasqueradeRequest,
  SupportMasqueradeOptionsResponse,
  SupportMasqueradeResponse,
  User,
  UsernameAvailabilityResult,
} from '@kangaroo/shared';

import { api } from '../lib/api';

export async function login(payload: LoginRequest) {
  const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', payload);
  return response.data.data;
}

export async function register(payload: RegisterRequest) {
  const response = await api.post<ApiResponse<AuthResponse>>('/auth/register', payload);
  return response.data.data;
}

export async function authenticateWithGoogle(payload: GoogleAuthRequest) {
  const response = await api.post<ApiResponse<AuthResponse | GoogleAuthSetupRequiredResponse>>('/auth/google', payload);
  return response.data.data;
}

export async function authenticateWithApple(payload: AppleAuthRequest) {
  const response = await api.post<ApiResponse<AuthResponse | AppleAuthSetupRequiredResponse>>('/auth/apple', payload);
  return response.data.data;
}

export async function completeRegistration(payload: CompleteRegistrationRequest) {
  const response = await api.post<ApiResponse<AuthResponse>>('/auth/complete-registration', payload);
  return response.data.data;
}

export async function checkUsernameAvailability(username: string) {
  const response = await api.get<ApiResponse<UsernameAvailabilityResult>>('/auth/usernames/availability', {
    params: { username },
  });
  return response.data.data;
}

export async function fetchCurrentUser() {
  const response = await api.get<ApiResponse<User>>('/auth/me');
  return response.data.data;
}

export async function fetchSupportMasqueradeOptions(params?: { q?: string; teamId?: string }) {
  const response = await api.get<ApiResponse<SupportMasqueradeOptionsResponse>>('/auth/masquerade/options', { params });
  return response.data.data;
}

export async function startSupportMasquerade(payload: StartSupportMasqueradeRequest) {
  const response = await api.post<ApiResponse<SupportMasqueradeResponse>>('/auth/masquerade/start', payload);
  return response.data.data;
}

export async function updateCurrentUser(payload: { name?: string; avatarUrl?: string | null; measurementPreferences?: MeasurementPreferences | null }) {
  const response = await api.patch<ApiResponse<User>>('/auth/me', payload);
  return response.data.data;
}

export async function changePassword(payload: { currentPassword: string; newPassword: string }) {
  await api.post('/auth/change-password', payload);
}

export async function deleteCurrentUserAccount() {
  await api.delete('/auth/me');
}

export async function verifyEmail(token: string) {
  const response = await api.post<ApiResponse<{ user: User }>>('/auth/verify-email', { token });
  return response.data.data;
}

export async function resendVerification(email: string) {
  const response = await api.post<ApiResponse<{ message: string }>>('/auth/resend-verification', { email });
  return response.data.data;
}

export async function forgotPassword(email: string) {
  const response = await api.post<ApiResponse<{ message: string }>>('/auth/forgot-password', { email });
  return response.data.data;
}

export async function resetPassword(payload: { token: string; password: string }) {
  const response = await api.post<ApiResponse<AuthResponse>>('/auth/reset-password', payload);
  return response.data.data;
}

export async function uploadUserAvatar(image: File) {
  const formData = new FormData();
  formData.append('avatar', image);

  const response = await api.post<ApiResponse<User>>('/auth/me/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
}